import type { ApolloClient } from "@apollo/client/core";
import type { Dict } from "mixpanel-browser";
import type { ReactNode } from "react";

export type AnalyticsProperties = Dict;

export interface AuthenticatedUser {
  name: string;
  username?: string;
  itdId?: string;
  roles: string[];
}

export enum Team {
  CORE = "CORE",
  CREATOR = "CREATOR",
  INTEGRATEX = "INTEGRATEX",
  INTEGRATION = "INTEGRATION",
  PLATFORM = "PLATFORM",
  PRODUCTFACTORY = "PRODUCTFACTORY",
  REACTOR = "REACTOR",
  SAXONEERS = "SAXONEERS",
  UX = "UX",
  WEBDEV = "WEBDEV",
  OTHER = "OTHER",
}

export enum Role {
  DEVELOPER = "DEVELOPER",
  PRODUCTOWNER = "PRODUCTOWNER",
  OTHER = "OTHER",
}

export interface AppConfiguration {
  team: Team;
  role: Role;
}

export interface Notification {
  inAppText: ReactNode;
  /** Defaults to short (5 seconds). Long lasts 20 seconds; persistent stays until closed. */
  inAppExpiry?: "short" | "long" | "persistent";
  /** Also notify via browser notifications, when permission is granted (call askForBrowserNotificationPermission() before). */
  browserNotificationText?: string;
  status: "success" | "info" | "warning" | "error";
}

export type BrowserTitleStatus = "running" | "completed" | "failed" | "none";

export interface AppFrameApi {
  showNotification: (notification: Notification) => void;
  /** Queues the shared Puffin completion animation. */
  showTaskCompletedAnimation: () => void;
  /**
   * Offers an in-app opt-in unless permission is already decided, unavailable, or questions are suppressed.
   * May be called while observing task state; only the dialog's Yes action requests native permission.
   */
  askForBrowserNotificationPermission: (reason: string) => void;
  /** Applies to the current pathname only; never persisted. */
  updateBrowserTitleStatus: (status: BrowserTitleStatus) => void;
  getTestTurtleClient: () => ApolloClient;
  getDeployDuckClient: () => ApolloClient;
  getPlatformPlatypusClient: () => ApolloClient;
  getAccessToken: () => Promise<string | undefined>;
  getUser: () => Promise<AuthenticatedUser | undefined>;
  getAppConfiguration: () => AppConfiguration;
  trackAnalytics: (eventName: string, props: AnalyticsProperties) => void;
}
